// useControlPanelData — everything the control panel draws, from one call.
//
// This composes the SAME hooks the OR board and the timeline already use. It
// opens no listener of its own beyond the assignments stream that
// TimelineChartPage also opens, so adding the panel to a session costs nothing
// new except that one query.
//
// ⚠️ FACILITY SCOPE IS NOT A PARAMETER YOU CAN TAKE LIBERTIES WITH.
// `AlertProvider` derives its facility from the signed-in account
// (`AlertContext.js:41` — `user?.activeFacilityId`), and it is not overridable.
// So a panel rendered for any OTHER facility would show rooms and staffing
// correctly while SILENTLY losing every emergency, urgency and break request:
// no error, no empty state, just a board that looks calm. Since a break request
// lives only in the alert bus, that also erases the entire "who is waiting"
// signal.
//
// The caller must therefore pass the account's own facility. `ControlPanelPage`
// enforces it, and the geofence work in a later slice CONFIRMS that facility
// rather than switching it.
//
// 2026-08-07: that is no longer the only defence. The mismatch is now DETECTABLE
// — `AlertProvider` publishes `alertFacilityId`, and this hook returns
// `alertsCovered`. A page that genuinely does browse other campuses
// (`TimelineChartPage`, the first one in the app with a free switcher) can now
// branch on it and refuse to draw the alarm half, instead of drawing a calm lie.
// The comment above still holds for callers that have a choice: passing the
// account's own facility remains the right default.

import { useEffect, useMemo, useState } from "react";
import { useRooms } from "./useRooms";
import { useLocations } from "./useLocations";
import { useDailyRoster, rosterDateKey } from "./useDailyRoster";
import { useStaff } from "./useStaff";
import { useBreakBoardRows } from "./useBreakBoardRows";
// The names on today's sheet the import could not match to a person. The engine
// has always recorded them; until 2026-09-11 nothing read them.
import { useSheetEngineQuestions } from "./useSheetEngineQuestions";
import { useAlertCenter, alertsCoverFacility } from "../context/AlertContext";
import { listenToAssignments, listenToTransfers } from "../firebase/firestore";
import { buildTimeline, attachLocumLeaves, to12h, hmToMinutes, rowClaimsAt } from "../domain/timeline";
import { ROOMS_PER_DOCTOR } from "../domain/timeline";
import { doctorSteps } from "../domain/doctorSteps";
import { withDeparted } from "../domain/secondProvider";
import { flattenLanes, alertQueue, nextBoundary, outstandingItems } from "../domain/controlPanel";
import { stagedReliefRows } from "../domain/panelAssign";
import { breakWaitingRows as buildBreakWaitingRows } from "../domain/breakWaiting";
import { ALERT_TYPES, isLiveAlert } from "../domain/alerts";
import { coverageStatus, unstaffedRooms } from "../domain/coverage";
import { buildCrnaShiftGroups, buildMdCallList } from "../domain/tvPanels";
import { roomForOrRoom } from "../domain/gridTemplate";
import { isTbdRoom } from "../domain/locations";
import { normalizeStatus, endedRoomIds } from "../domain/status";
// ⚠️ IMPORT **AND** RE-EXPORT. `export { benchProviders } from "..."` alone is
// a pure re-export: it forwards the name to importers WITHOUT creating a
// binding in this module's scope, and this file CALLS benchProviders itself
// further down. That form compiles, passes the suite, and then throws
// `ReferenceError` at runtime — the exact trap RoomCard's smartName hit.
import { benchProviders, breakGivers } from "../domain/bench";

export { benchProviders };

// ── Pure derivations for the right-hand cards ────────────────────────────────
//
// These belong to `src/domain/` by the house rule, and they would live in
// `domain/controlPanel.js` — except that file is already 526 lines, over the
// 500-line ceiling in CLAUDE.md. So they sit at MODULE scope here: still pure,
// still React-free, still exported for direct unit testing, and still out of
// the component tree. Fold them into `domain/controlPanel.js` when it is split.
//
// `benchProviders` has already left — it moved to `domain/bench.js` once the
// mobile break board needed it, because importing it from HERE would have
// pulled AlertContext and firebase/firestore into the lazy mobile chunk for one
// pure helper. It is imported and RE-EXPORTED below so existing call sites and
// tests that import it from this module keep working.

// Doctors on today's roster (AIC + supervisors) supervising no room today.
export function unassignedDoctors(doctorEntries = [], assignments = [], nowMin = null) {
  // ⚠️ A SUPERVISOR IS RELEASED BY THE SAME RULE AS THE PROVIDER. Owner,
  // 2026-08-24: "as soon as there are no more cases in the room, the providers
  // AND the supervisors are released to free now." `rowClaimsAt` is the one
  // definition of "this row still holds you", shared with `benchProviders` —
  // written twice, the two lists would eventually disagree about whether the
  // CRNA and their attending are free, and the board would show a room's two
  // halves in different states.
  const attending = new Set(
    (Array.isArray(assignments) ? assignments : [])
      .filter((a) => rowClaimsAt(a, nowMin))
      .map((a) => a && a.attendingId)
      .filter(Boolean)
  );
  // ⚠️ TWO FIELDS CAN REMOVE A DOCTOR, AND "SUPERVISING NO ROOM" IS ONLY ONE
  // OF THEM. Going home empties this person's `attendingId` everywhere —
  // DepartureFlow makes the user reassign every room they held — so the
  // claim test above passes for exactly the people who have LEFT, and sending
  // somebody home promoted them into FREE NOW. The CRNA side has always been
  // closed (`benchProviders` filters `departed`); this one was not.
  //
  // The dangerous direction is that it hides a SHORTAGE: FREE NOW is the list
  // an AIC reads at 19:30 to find a supervisor for an add-on, and a name on it
  // that is already at home costs a phone call and a delayed room.
  //
  // `departed` is MARKED, not filtered, by `withDeparted` (secondProvider.js),
  // which is what keeps the MD Call rail's struck-through DEPARTED row intact;
  // filtering belongs here, at the point where the list means "you can send for
  // this person". `leaveMin` is the locum's stated leaving hour, attached by
  // `attachLocumLeaves` — a fact from the roster, not a status anyone has to
  // remember to press.
  //
  // With no clock we cannot say anyone has left, so the leave test is skipped
  // rather than guessed: an empty FREE NOW is the less dangerous error.
  return (Array.isArray(doctorEntries) ? doctorEntries : []).filter(
    (d) =>
      d &&
      d.id &&
      !attending.has(d.id) &&
      !d.departed &&
      !(nowMin != null && Number.isFinite(d.leaveMin) && d.leaveMin <= nowMin)
  );
}

// Lanes running a case with nobody named as the supervising MD. `attending` is
// the field `buildTimeline` puts on the case (`timeline.js:429`); a lane with no
// cases is not "missing an MD", it is simply not running.
export function roomsMissingMd(lanes = []) {
  return (Array.isArray(lanes) ? lanes : [])
    .filter((l) => l && l.cases && l.cases.length > 0 && !l.cases.some((c) => c && c.attending))
    .map((l) => ({ roomId: l.roomId, roomName: l.roomName }));
}

const normName = (n) => String(n || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();

// Today's MD call list, each row carrying whether the doctor is a LOCUM.
//
// ⚠️ "L" ON THE CALL LIST MEANS LOCUM, NEVER "LAST". The sheet marks a locum by
// giving them NO call number, so `callOrder == null` on a call-list entry is the
// designation — the same rule `TimelineChartPage.js:98-104` applies. Rendering
// it as "#L" or sorting them as "last on call" would tell the board-runner the
// opposite of the truth about who they can ring.
//
// The absent-call-number rule is applied ONLY to people who appear on
// `roster.mdCallList`; a supervisor who is simply missing a call number in the
// roster is unknown, not a locum.
export function mdCallRows(roster, staffDoctors = []) {
  const calls = Array.isArray(roster?.mdCallList) ? roster.mdCallList.filter(Boolean) : [];
  const times = roster?.locumLeaveTimes && typeof roster.locumLeaveTimes === "object"
    ? roster.locumLeaveTimes
    : {};
  const staff = Array.isArray(staffDoctors) ? staffDoctors : [];
  const staffById = (id) => staff.find((s) => s && (s.id === id || s.uid === id)) || null;
  const leaveOf = (code) => {
    const min = hmToMinutes(times[code]);
    return min == null ? "" : to12h(min);
  };

  const rows = buildMdCallList(roster, staff).map((m) => {
    const st = staffById(m.id);
    const code = String(st?.shortCode || "").toUpperCase();
    const entry =
      (code && calls.find((e) => String(e.code || "").toUpperCase() === code)) ||
      calls.find((e) => normName(e.name) === normName(m.name)) ||
      null;
    const entryCode = String(entry?.code || code || "").toUpperCase();
    const isLocum = Boolean(
      entry?.isLocum ||
        st?.employmentType === "locums" ||
        (entry && entry.callOrder == null && m.callOrder == null)
    );
    return {
      id: m.id,
      name: m.name,
      phone: m.phone || "",
      callOrder: m.callOrder,
      isAic: m.isAic,
      isLocum,
      leaveLabel: entryCode ? leaveOf(entryCode) : "",
    };
  });

  // ⚠️ THE CALL LIST ON THIS BOARD IS THE WHOLE TENANT'S, NOT THIS CAMPUS'S.
  // A global sheet import writes ONE parsed call list to EVERY facility's board
  // — `GlobalSheetImport.js:246-258` passes the same `plan.mdCall` inside its
  // `for (const fid of tenantFacilityIds)` loop. So `roster.mdCallList` here
  // names attendings across all campuses.
  //
  // This function used to append every leftover call-list entry that carried no
  // call number, labelling each one "Locum". At Wesley Chapel that printed SEVEN
  // rows for doctors working at other campuses — including `NED`, a code with no
  // staff record at all — under a heading a board-runner reads as "who I can
  // ring here". Reported live 2026-08-13.
  //
  // THE SHEET SUPPLIES THE BADGE; THIS FACILITY'S ROSTER SUPPLIES THE
  // MEMBERSHIP. `buildMdCallList` above is already roster-scoped (the board's
  // AIC + `roster.supervisors`), so a locum genuinely working here is ALREADY
  // in `rows`: `notesLocums` puts locums onto the roster as unassigned
  // supervisors carrying their cover window (notesLocums.js:13-16), which is
  // how they reach the bench and the staffing math. Nothing that belongs to
  // this campus is lost by refusing the leftovers — only other campuses' people
  // are, and naming them here was never right.
  //
  // If a future sheet ever writes a genuinely per-facility call list, scope it
  // by facility HERE rather than reviving a blanket append.
  return rows;
}

// `options` is destructured to primitives immediately so an inline object
// literal at the call site cannot churn every memo below it.
// THE CLOCK ONLY APPLIES TO THE DAY IT CAME FROM.
//
// ⚠️ SIX CALLERS PASS A RAW `nowMin` — the Timeline, the three board variants,
// the light board and the mobile timeline — and every one of them sits behind a
// date picker. Handing today's minute-of-day to code that asks "has this row
// finished yet" is wrong on any other date, in both directions: open tomorrow's
// board at 2 PM and an 07:00-11:00 case reads as already over, so its provider
// is dropped off the BREAKS card and shown on the bench for a day that has not
// started; open yesterday's and people read as still holding rooms that ended a
// day ago.
//
// Null is the FAIL-CLOSED answer, not a missing one: `rowClaimsAt(row, null)`
// keeps every row, because no clock means no evidence that anybody finished.
// Exported so the rule is testable without mounting a hook that opens five
// listeners — the same reason `unassignedDoctors` is exported above.
// ⚠️ ONE FROZEN EMPTY ARRAY, NOT A FRESH `[]` PER RENDER. A default of `[]` is
// a new identity every time the hook runs, so `outstanding`'s useMemo — which
// lists `tasks` among its dependencies — recomputed on every render for the
// five callers that pass none (the three board variants and the light
// timeline), and handed a new `outstanding` identity to every memoized child
// below it. A shared constant makes the no-tasks case genuinely stable.
const EMPTY_TASKS = Object.freeze([]);

export function claimClockFor(dateKey, nowMin, today = rosterDateKey()) {
  return dateKey === today ? nowMin : null;
}

export function useControlPanelData(facilityId, dateKey, options = {}) {
  // `supervisionRatio` is the FACILITY rule (facilityRules.supervisionRatio),
  // defaulted so the five callers that do not pass one are unchanged. It has to
  // arrive here rather than be re-resolved downstream: the adequacy card and
  // the Doctors Needed box are both built from this hook, and a page that fed
  // them different ratios would put two answers to one question on one screen.
  const {
    nowMin = null,
    breakWindows = null,
    supervisionRatio = ROOMS_PER_DOCTOR,
    // Today's directives, passed IN rather than subscribed to here: the page
    // already holds one `useTasks` subscription for the task card, and a second
    // on the same day-scoped query is the duplicate-listener trap this file
    // records for `useStaff`. Defaults empty, so a caller that passes none
    // simply gets no directive escalation.
    tasks = EMPTY_TASKS,
  } = options;

  // ⚠️ THE CLOCK IS GATED HERE, NOT AT THE CALL SITE — six callers pass a raw
  // `nowMin` (this hook is used by the Timeline, the three board variants, the
  // light board and the mobile timeline), every one of them behind a date
  // picker, and every one of them was handing TODAY'S minute-of-day to code
  // that asks "has this row finished yet".
  //
  // Open tomorrow's board at 2 PM and an 07:00-11:00 case reads as already
  // over: its provider is dropped off the BREAKS card and shown on the bench,
  // for a day that has not started. Open yesterday's and people read as still
  // holding rooms that ended a day ago.
  //
  // `rowClaimsAt(row, null)` keeps every row, which is the fail-closed
  // direction — no clock means no evidence anyone has finished. Derived from
  // this hook's own `dateKey` so a future caller cannot reintroduce it.
  const claimMin = claimClockFor(dateKey, nowMin);
  const { questions: sheetQuestions, feed: sheetFeed } = useSheetEngineQuestions(facilityId, dateKey);
  const { rooms } = useRooms(facilityId);
  const { locations } = useLocations(facilityId);
  const { roster } = useDailyRoster(facilityId, dateKey);
  const { doctors: staffDoctors, providers: staffProviders } = useStaff();
  // The alert bus is consumed, never re-opened (`AlertProvider` owns the one
  // subscription, App.js:105). The lifecycle actions come from the same place
  // so the cards write through the tested helpers rather than a second path.
  const {
    alertFacilityId,
    alerts,
    emergencyRoomIds,
    breakRequestRoomIds,
    acknowledge,
    resolve,
    canClear,
  } = useAlertCenter();

  // Does the alert bus actually cover the facility we were asked about? The bus
  // follows the signed-in account and cannot be redirected, so this is the one
  // question that decides whether the alarm half of this hook may be drawn at
  // all. Fails closed.
  const alertsCovered = alertsCoverFacility(alertFacilityId, facilityId);

  const [assignments, setAssignments] = useState([]);
  const [assignmentsReady, setAssignmentsReady] = useState(false);
  useEffect(() => {
    if (!facilityId || !dateKey) {
      setAssignments([]);
      setAssignmentsReady(false);
      return undefined;
    }
    setAssignmentsReady(false);
    const unsub = listenToAssignments(facilityId, dateKey, (snap) => {
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAssignmentsReady(true);
    });
    return unsub;
  }, [facilityId, dateKey]);

  // Traveling people, the same source the board's Traveling box uses.
  const [transfers, setTransfers] = useState([]);
  useEffect(() => {
    if (!facilityId || !dateKey) { setTransfers([]); return undefined; }
    const unsub = listenToTransfers(facilityId, dateKey, (snap) =>
      setTransfers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [facilityId, dateKey]);

  // The day's doctors: AIC + supervisors, deduped. Doctors span shift
  // boundaries, so unlike CRNAs they carry no departure — same rule the
  // timeline uses.
  const rosterDoctorEntries = useMemo(
    () =>
      Array.from(
        new Map(
          [
            ...(roster?.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
            ...(roster?.supervisors || []),
          ]
            .filter((m) => m && m.id)
            .map((m) => [m.id, m])
        ).values()
      ),
    [roster]
  );

  // `buildTimeline` is consumed UNCHANGED. The panel adds nothing to the model
  // and removes nothing from it, so a wall and the existing /timeline page
  // rendering the same facility and date must produce identical output — which
  // is exactly how this slice is verified.
  // ⚠️ HOISTED, AND SHARED. This used to be built inline inside the `buildTimeline`
  // call. `doctorSteps` needs the SAME list — locum leaving times attached and
  // all — and building it a second time would be two lists that agree only until
  // someone edits one of them.
  // ⚠️ DOCTORS GET `withDeparted` TOO, and did not until 2026-09-07. The CRNA
  // list below has always been wrapped (:307); the doctor list was not, and
  // nothing downstream consulted `departed` for doctors — so a doctor who had
  // gone home was still counted as ON CALL and as RELEASABLE by the Doctors
  // Needed box. Caught live: departing Dr. Alvarez left the box reading
  // "3 on call · 3 can go" while the MD Call rail beside it already said
  // DEPARTED. The dangerous direction is that it hides a SHORTAGE — available
  // is inflated, so `required > available` fires late or not at all.
  //
  // `withDeparted` MARKS rather than filters (secondProvider.js:369), which is
  // what keeps the rail's struck-through DEPARTED row intact; the coverage
  // maths in prepareDoctors is what skips them.
  const timelineDoctors = useMemo(
    () =>
      attachLocumLeaves(
        withDeparted(rosterDoctorEntries, roster?.departed),
        roster?.mdCallList,
        roster?.locumLeaveTimes,
        staffDoctors
      ),
    [rosterDoctorEntries, roster, staffDoctors]
  );

  const model = useMemo(
    () =>
      buildTimeline(
        rooms,
        locations,
        assignments,
        withDeparted(roster?.crnas, roster?.departed),
        timelineDoctors,
        supervisionRatio
      ),
    [rooms, locations, assignments, roster, timelineDoctors, supervisionRatio]
  );

  // When the doctor requirement changes across the day, and how many of today's
  // doctors are genuinely finished. Gated on `claimMin`, not `nowMin`: on any
  // board but today's the projection still stands (it is schedule maths) but the
  // live reading is withheld, because a count derived from a null clock and a
  // count from a genuinely empty board render identically.
  const doctorPlan = useMemo(
    () =>
      doctorSteps(model.placed, timelineDoctors, {
        ratio: supervisionRatio,
        nowMin: claimMin,
        // ⚠️ CANCELLED ROOMS ARE NOT COUNTED. The timeline builds its lanes from
        // assignment rows and never reads a room's status, so a cancelled room
        // went on demanding a supervising doctor all day.
        endedRoomIds: endedRoomIds(rooms),
      }),
    [model.placed, timelineDoctors, supervisionRatio, claimMin, rooms]
  );

  const lanes = useMemo(() => flattenLanes(model.sections), [model.sections]);
  const laneIdList = useMemo(() => lanes.map((l) => l.roomId), [lanes]);

  // ⚠️ The ONLY correct source for break cells. A requested break is never
  // written to `room.breaks` — `requestMyBreak` raises a BREAK_REQUEST alert and
  // nothing else — so a panel reading room docs alone would show an empty cell
  // for someone who is actively asking for cover. This shared builder is where
  // the alert bus and the room doc are merged, and a done or in-progress channel
  // always outranks a stale request.
  const breakRows = useBreakBoardRows(
    roster?.crnas || [],
    roster?.departed || {},
    rooms,
    assignments,
    alerts,
    // The same clock the bench and FREE NOW already run on, so a finished room
    // releases its provider from all three at the same minute — and the same
    // date gate, so none of the three releases anybody on another day's board.
    claimMin
  );

  const breakRowByRoomId = useMemo(() => {
    const map = new Map();
    for (const row of breakRows || []) {
      if (!row) continue;
      const room = row.roomName ? roomForOrRoom(row.roomName, rooms) : null;
      const id = room?.id || row.roomId;
      if (id) map.set(id, row);
    }
    return map;
  }, [breakRows, rooms]);

  // The OB-style "must always be covered" rule, resolved ONCE — to ids for the
  // lane wash and the chips, and to a described row for the alarm.
  //
  // ⚠️ THE DESCRIBED FORM IS NOT A LUXURY. `outstandingItems` used to find the
  // uncovered room by filtering LANES, and a lane exists only for a room with a
  // case on the chart. Since a standing OB watch now draws no case
  // (domain/timeline.isStandingOnCall), the id alone would have matched no lane
  // and the alarm would have disappeared at the exact moment the on-call CRNA
  // walked into another room. This carries the room, the person and where they
  // went, straight off the assignment rows `coverageStatus` already judged.
  const uncoveredDetail = useMemo(() => {
    const out = [];
    for (const asg of assignments) {
      if (!asg || !asg.isOnCall) continue;
      const room = roomForOrRoom(asg.orRoom, rooms);
      if (!room) continue;
      const status = coverageStatus(asg, assignments, room);
      if (!status.uncovered) continue;
      // The away room is an `orRoom` string on the assignment; resolve it to the
      // board's own room name so the alarm names the room the way the board
      // labels it, rather than however the sheet spelled it.
      const awayRoom = status.awayRoomName
        ? roomForOrRoom(status.awayRoomName, rooms)
        : null;
      out.push({
        roomId: room.id,
        roomName: room.name || asg.orRoom || "",
        providerName: String(asg.ahpName || "").trim(),
        reason: status.reason,
        awayRoomName: awayRoom?.name || status.awayRoomName || "",
      });
    }
    // ⚠️ AND ROOMS RUNNING WITH NOBODY IN THEM, which this list has never
    // contained. The loop above opens with `if (!asg.isOnCall) continue`, so the
    // only rows it examines are on-call watches — the OB rule — while the alarm
    // it feeds is worded generally and NEEDS YOU NOW claims to watch "rooms
    // running without a provider". WOR 12 ran a real case with no CRNA all
    // morning on 2026-09-13 and no surface said so. See domain/coverage.
    for (const r of unstaffedRooms(assignments, rooms, claimMin)) {
      if (out.some((x) => x.roomId === r.roomId)) continue;
      out.push(r);
    }
    return out;
  }, [assignments, rooms, claimMin]);

  const uncoveredRoomIds = useMemo(
    () => new Set(uncoveredDetail.map((r) => r.roomId)),
    [uncoveredDetail]
  );

  const scheduledRoomIds = useMemo(() => {
    const set = new Set();
    for (const a of assignments) {
      if (!a || !a.orRoom) continue;
      const room = roomForOrRoom(a.orRoom, rooms);
      if (room) set.add(room.id);
    }
    return set;
  }, [assignments, rooms]);

  const occupiedRooms = useMemo(
    () =>
      (Array.isArray(rooms) ? rooms : []).filter(
        (r) => r && (normalizeStatus(r.status) !== "empty" || scheduledRoomIds.has(r.id))
      ),
    [rooms, scheduledRoomIds]
  );

  // The board renders Home and Traveling as their own sections, not as counts.
  // A person who is off the board and in neither is invisible, which is the
  // failure the placement flow exists to prevent — so the panel shows them the
  // same way.
  const departedMembers = useMemo(() => {
    const departed = roster?.departed || {};
    return (roster?.crnas || []).filter((c) => c && c.id && departed[c.id]);
  }, [roster]);

  const travelingMembers = useMemo(() => transfers || [], [transfers]);

  const alarms = useMemo(
    () => ({ emergencyRoomIds, breakRequestRoomIds, uncoveredRoomIds }),
    [emergencyRoomIds, breakRequestRoomIds, uncoveredRoomIds]
  );

  // ── The right-hand cards ───────────────────────────────────────────────────

  // ⚠️ EMERGENCIES ONLY, deliberately. An `urgency` is a SILENT, private ping to
  // one doctor whose privacy is enforced in the UI (`alerts.js:6-10`, `:88-92`),
  // and a command panel that listed everyone's silent pings on a shared screen
  // would break that contract from the one surface most likely to be on a wall.
  // Break requests have their own card below.
  const alertRows = useMemo(
    () =>
      alertQueue(
        (alerts || []).filter((a) => a && a.type === ALERT_TYPES.EMERGENCY && isLiveAlert(a)),
        Date.now()
      ),
    [alerts]
  );

  // Relief that has been STAGED but not yet confirmed — a handover in flight.
  // Reuses the same tested builder the boards use, so this cannot drift from
  // what a board shows; `nowMin` drives its staleness, which is why it is a
  // parameter rather than a clock read.
  const reliefRows = useMemo(
    () =>
      stagedReliefRows(assignments, {
        nowMin,
        resolveRoom: (o) => roomForOrRoom(o, rooms),
        // ⚠️ WITHOUT THIS, AN MD HANDOVER IS JUDGED BY THE ROOM CRNA'S CLOCK.
        // A doctor's departure is not on the assignment row at all — it lives on
        // the roster — so a handover between two attendings could only ever read
        // as calm and on-plan, however late, and a handover to a doctor who had
        // gone home read the same way.
        isDeparted: (id) => !!id && !!(roster?.departed || {})[id],
      }),
    [assignments, nowMin, rooms, roster?.departed]
  );

  // ⚠️ A REAL INSTANT, not minutes-past-midnight: the unanswered-directive age
  // must survive midnight and a device in another timezone (see taskWaitedMin).
  //
  // ⚠️ SAMPLED ON THE CLOCK TICK, NOT INSIDE THE WORKLIST MEMO. `Date.now()`
  // there was re-read on EVERY Firestore snapshot, so an age jittered between
  // ticks and could cross the ten-minute threshold off-clock; it also made that
  // memo impure, which double-renders differently under StrictMode. Keyed on
  // `claimMin` — the same day-gated clock every other rule here uses, so a past
  // or future board gets no instant and claims no age.
  //
  // Declared HERE, above the breaks queue, because that queue now dates its own
  // rows: two surfaces computing the same age from two clocks is how the strip
  // and the worklist come to disagree about who has waited longest.
  const nowMs = useMemo(
    () => (Number.isFinite(claimMin) ? Date.now() : null),
    [claimMin]
  );

  // ⚠️ WAITING HAS TWO RECORDS AND THIS READS BOTH. A request lives only in the
  // alert bus (`requestMyBreak` writes nothing to the room doc); a cover NAMED
  // by the AIC lives only on the room doc (`assignCoverToRoom` raises no alert).
  // Reading alerts alone reported `0 · Nobody is waiting for a break` beside a
  // room whose provider had a named cover who had not arrived — see
  // domain/breakWaiting for the live case and the one-row-per-room rule.
  const breakWaitingRows = useMemo(
    () => buildBreakWaitingRows({ alerts, rooms, assignments, nowMs, nowMin: claimMin }),
    [alerts, rooms, assignments, nowMs, claimMin]
  );

  const available = useMemo(
    () => ({
      providers: benchProviders(roster, assignments, claimMin),
      // ⚠️ THE SAME LIST THE TIMELINE USES, NOT THE RAW ROSTER. `timelineDoctors`
      // is `rosterDoctorEntries` with `departed` marked and locum leaving times
      // attached; passing the raw entries here was how a doctor who had gone
      // home reached FREE NOW. Two lists built from one roster will always
      // drift — build it once, above, and share it.
      doctors: unassignedDoctors(timelineDoctors, assignments, claimMin),
      // ⚠️ NOT `providers.filter(givingBreaks)`. A break-giver who is holding a
      // room is not on the bench, so filtering the bench could never find them
      // — which is why the label was bench-only until somebody tried to set it
      // on a CRNA in a room. `breakGivers` reads the roster flag directly and
      // tags each person with the room they are standing in, or none.
      breakGivers: breakGivers(roster, assignments, claimMin),
    }),
    [roster, assignments, timelineDoctors, claimMin]
  );

  const mdCall = useMemo(() => mdCallRows(roster, staffDoctors), [roster, staffDoctors]);

  // uid → name, for naming people in the worklist. Built from the staff this
  // hook ALREADY subscribes to, so it costs no extra read. A directive names
  // recipients by auth uid; "somebody has not answered" is a row an AIC cannot
  // act on, and this is what turns it into a name they can ring.
  const nameByUid = useMemo(() => {
    const out = {};
    for (const person of [...(staffDoctors || []), ...(staffProviders || [])]) {
      if (person && person.uid && person.name) out[person.uid] = person.name;
    }
    return out;
  }, [staffDoctors, staffProviders]);

  const boundary = useMemo(
    () => nextBoundary(model.staffing, nowMin, model.doctorStaffing),
    [model.staffing, model.doctorStaffing, nowMin]
  );

  const outstanding = useMemo(
    () =>
      outstandingItems({
        nowMin,
        // The SAME day-gated clock `rowClaimsAt` gets, for the rules that ask
        // "is this over yet". Passed separately rather than replacing `nowMin`
        // because the break and departure rules below still read the raw one,
        // and quietly changing what they see is a different change from this.
        dayClockMin: claimMin,
        // A seat the board cannot NAME. The rules gate Room Status on the
        // room's providerId/supervisingDoctorId, so an unmatched attending
        // cannot acknowledge a phase — and nothing surfaced this before.
        sheetQuestions,
        // Whether the sheet is still REACHING the board. A parse refusal leaves
        // today's rows in place and stops updating them, so nothing else in
        // this fold can see it — see domain/controlPanel.
        sheetFeed,
        emergencies: alertRows.map((a) => ({
          roomId: a.roomId,
          roomName: a.roomName,
          acknowledgedBy: a.acknowledgedBy,
        })),
        uncoveredRoomIds,
        uncoveredRooms: uncoveredDetail,
        lanes,
        // `breakBoardRows` names the person on `name`; the fold reads
        // `providerName`, so the two are joined here rather than the card
        // falling back to a room number for a question about a person.
        breakRows: (breakRows || []).map((r) => ({ ...r, providerName: r.name })),
        breakWindows,
        staffing: model.staffing,
        roomsMissingMd: roomsMissingMd(lanes),
        // The flat cases, for the one-provider-two-rooms check. `placed` is the
        // only shape carrying a case's provider AND its minutes together, which
        // is exactly what an overlap needs.
        placed: model.placed,
        // Staged handovers — promised, not yet completed. Already computed above
        // for the status band; the fold reads the SAME rows so the strip and the
        // worklist cannot disagree about what is pending.
        reliefRows,
        // An unanswered or refused directive belongs on the worklist, not only
        // in the task card's grey "waiting" — see outstandingItems.
        tasks,
        nameByUid,
        nowMs,
      }),
    [nowMin, claimMin, alertRows, uncoveredRoomIds, uncoveredDetail, lanes, breakRows, breakWindows, model.staffing, model.placed, reliefRows, tasks, nameByUid, nowMs, sheetQuestions, sheetFeed]
  );

  // Per-source receipt times for the cards' "as of" stamps.
  //
  // These are NEUTRAL by design and must stay that way. A per-card colour ramp
  // measured from the last snapshot goes red on a healthy, quiet board — an OR
  // complex legitimately produces no writes for twenty minutes — and once
  // everything is red at lunchtime nobody reads red again. "Is this board live"
  // is answered ONCE, in the rail, by `connectionState`.
  const [freshness, setFreshness] = useState(() => ({
    alerts: null, rooms: null, assignments: null, roster: null,
  }));
  useEffect(() => { setFreshness((f) => ({ ...f, alerts: Date.now() })); }, [alerts]);
  useEffect(() => { setFreshness((f) => ({ ...f, rooms: Date.now() })); }, [rooms]);
  useEffect(() => { setFreshness((f) => ({ ...f, assignments: Date.now() })); }, [assignments]);
  useEffect(() => { setFreshness((f) => ({ ...f, roster: Date.now() })); }, [roster]);

  // Rail metrics. `nowMin` is a parameter rather than a clock read, so the
  // caller's single tick drives everything and two parts of the panel can never
  // disagree about what time it is.
  const railMetrics = useMemo(() => {
    const crnas = (roster?.crnas || []).filter((c) => c && c.id);
    const departed = roster?.departed || {};
    const onShiftList = crnas.filter((c) => !departed[c.id]);

    const groups = buildCrnaShiftGroups(roster, assignments);

    return {
      // ⚠️ TBD ROOMS EXCLUDED, the way every other surface counts them
      // (BoardPage.js:337, gridTemplate.js:43). TBD is an internal placeholder
      // that is never shown, so a raw `rooms.length` counted a room nobody can
      // see: /board said 23 while the boards and the timeline rail said 24 —
      // two numbers for one fact, on screens a reader compares side by side.
      // Fixed here rather than per-page so all three boards and the rail agree.
      roomsTotal: Array.isArray(rooms) ? rooms.filter((r) => !isTbdRoom(r)).length : 0,
      roomsRunning: model.occupiedCount,
      onShift: onShiftList.length,
      // Same list the Available-now card renders, so the rail count and the card
      // can never disagree about who is on the bench.
      bench: available.providers.length,
      shiftGroups: groups,
    };
  }, [roster, assignments, rooms, model.occupiedCount, available.providers.length]);

  return {
    model,
    // { now, steps, releasable, peakAhead } — see domain/doctorSteps. `now` and
    // `releasable` are NULL on a board that is not today.
    doctorPlan,
    lanes,
    laneIdList,
    // `breakRows` is published ONCE, further down beside the other alert-derived
    // cards. It was listed here as well — the same value under a duplicate key,
    // so the object was correct and Vite warned on every build. Deduped rather
    // than silenced: the surviving entry is the one carrying the alertsCovered
    // caveat a caller needs to read.
    breakRowByRoomId,
    // ⚠️ ALERT-DERIVED, AND ONLY MEANINGFUL WHEN `alertsCovered` IS TRUE.
    // `alarms`, `alertRows`, `breakWaitingRows`, the alarm half of `outstanding`,
    // and every lane's `top` all read the alert bus, which is scoped to the
    // ACCOUNT's facility and cannot be pointed elsewhere. When this hook is
    // called for any other facility they are not empty — they are WRONG, and
    // wrong in two directions at once (calm lanes, foreign room names).
    // A caller must branch on `alertsCovered` and refuse to draw. See
    // `alertsCoverFacility` in context/AlertContext.js for the full mechanism.
    alarms,
    railMetrics,
    // TRUE when the alert bus actually covers the facility this hook was called
    // for. False means: draw an explicit refusal for anything alert-derived.
    // Never `0`, never blank, and never an all-clear — "nothing outstanding" is
    // an assertion of safety, and here it would be a lie.
    alertsCovered,
    // Published so a refusal can name the campus the alerts DO cover, which is
    // the difference between "something is broken" and "you are looking
    // elsewhere". `undefined` means AlertProvider is not mounted at all.
    alertFacilityId,
    rooms,
    locations,
    // The board's own definition of a room worth showing: live status non-empty
    // OR scheduled on today's sheet (BoardPage isOccupied). The timeline model
    // is stricter — it keeps only rooms with a MATCHED CASE — so a room the
    // board displays can be missing from the model entirely. A command surface
    // must not show fewer rooms than the board it is meant to replace.
    occupiedRooms,
    departedMembers,
    travelingMembers,
    roster,
    assignments,
    staffDoctors,
    staffProviders,
    // Cards
    alertRows,
    reliefRows,
    breakWaitingRows,
    // Per-provider break rows (channel state merged from the room doc and the
    // alert bus). Alert-derived in part, so gate on `alertsCovered`.
    breakRows,
    available,
    mdCall,
    boundary,
    outstanding,
    freshness,
    // Alert lifecycle actions, passed straight through from AlertProvider.
    acknowledge,
    resolve,
    canClear,
    // Rooms arriving empty is a legitimate state (a facility with no board yet),
    // so readiness tracks the assignments listener rather than guessing from
    // emptiness.
    loading: !assignmentsReady,
  };
}

export default useControlPanelData;
